import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCattle } from '@/contexts/CattleContext';
import { useHealth } from '@/contexts/HealthContext';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

export const DiagnosisDialog = ({ open, onOpenChange }) => {
  const { cattle } = useCattle();
  const { addDiagnosis, veterinarians } = useHealth();
  
  const [formData, setFormData] = useState({
    cattleId: '',
    condition: '',
    diagnosedBy: '',
    diagnosisDate: new Date().toISOString().split('T')[0],
    severity: '',
    status: 'En tratamiento',
    symptoms: '',
    treatment: '',
    cost: '',
    notes: ''
  });

  const severityLevels = ['Leve', 'Moderada', 'Alta', 'Crítica'];
  const statusOptions = ['En tratamiento', 'Tratado', 'Resuelto', 'Crónico'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cattleId || !formData.condition || !formData.diagnosedBy) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    const selectedCattle = cattle.find(c => c.id === formData.cattleId);
    addDiagnosis({
      ...formData,
      cost: parseFloat(formData.cost) || 0
    });
    
    toast.success('Diagnóstico registrado', {
      description: `${formData.condition} en ${selectedCattle?.name}`
    });
    
    // Reset and close
    setFormData({
      cattleId: '',
      condition: '',
      diagnosedBy: '',
      diagnosisDate: new Date().toISOString().split('T')[0],
      severity: '',
      status: 'En tratamiento',
      symptoms: '',
      treatment: '',
      cost: '',
      notes: ''
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Diagnóstico</DialogTitle>
          <DialogDescription>
            Complete la información del diagnóstico y tratamiento
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Animal Selection */}
          <div className="space-y-2">
            <Label>Animal *</Label>
            <Select value={formData.cattleId} onValueChange={(value) => setFormData(prev => ({ ...prev, cattleId: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccione el animal" />
              </SelectTrigger>
              <SelectContent>
                {cattle.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.tag} - {c.name} ({c.breed})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Condition */}
          <div className="space-y-2">
            <Label>Condición / Enfermedad *</Label>
            <Input
              placeholder="Ej: Parasitosis, Mastitis, etc."
              value={formData.condition}
              onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
              required
            />
          </div>

          {/* Severity and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Severidad</Label>
              <Select value={formData.severity} onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione severidad" />
                </SelectTrigger>
                <SelectContent>
                  {severityLevels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione estado" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Diagnosed By and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Diagnosticado por *</Label>
              <Select value={formData.diagnosedBy} onValueChange={(value) => setFormData(prev => ({ ...prev, diagnosedBy: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione veterinario" />
                </SelectTrigger>
                <SelectContent>
                  {veterinarians.filter(v => v.specialty || v.license).map(v => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name} {v.specialty && `(${v.specialty})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha de Diagnóstico</Label>
              <Input
                type="date"
                value={formData.diagnosisDate}
                onChange={(e) => setFormData(prev => ({ ...prev, diagnosisDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Symptoms */}
          <div className="space-y-2">
            <Label>Síntomas</Label>
            <Textarea
              placeholder="Describa los síntomas observados..."
              value={formData.symptoms}
              onChange={(e) => setFormData(prev => ({ ...prev, symptoms: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Treatment */}
          <div className="space-y-2">
            <Label>Tratamiento Indicado</Label>
            <Textarea
              placeholder="Describa el tratamiento prescrito..."
              value={formData.treatment}
              onChange={(e) => setFormData(prev => ({ ...prev, treatment: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Cost */}
          <div className="space-y-2">
            <Label>Costo Total ($)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Incluye consulta, medicamentos, etc."
              value={formData.cost}
              onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas Adicionales</Label>
            <Textarea
              placeholder="Observaciones, recomendaciones, seguimiento..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              <Check className="mr-2 h-4 w-4" />
              Registrar Diagnóstico
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
